import { Component, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { TableModule } from "@openng/optimus-ui/table";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { CheckboxModule } from "@openng/optimus-ui/checkbox";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { RolesService } from "../../../../services/api/roles/roles.service";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { MessageService } from "@openng/optimus-ui/api";
import { NgClass } from "@angular/common";
import { Role } from "../../../../models/api/role";
import { MatrixRow, NotificationPermission, MatrixPermissionType } from "../../../../models/ui/permissionMatrix";
import { first } from "rxjs";
import { PERMISSION } from "../../../../enums/permissions.enum";
import { NotificationPermissionsComponent } from "../../components/notification-permissions/notification-permissions.component";
import { notificationPermissionCategoryMapping } from "../../../../models/api/notification/notification-channel-settings";

@Component({
  selector: "app-role",
  standalone: true,
  imports: [
    TableModule,
    CheckboxModule,
    FormsModule,
    TranslateModule,
    ButtonComponent,
    InputTextModule,
    ReactiveFormsModule,
    NgClass,
    NotificationPermissionsComponent,
  ],
  templateUrl: "./role.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./role.component.scss",
})
export class RoleComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);

  public actionType: "edit" | "add" = this.config.data.actionType;

  private permissions = inject(PermissionService);
  private rolesService = inject(RolesService);
  private message = inject(MessageService);
  private translate = inject(TranslateService);

  private allRemotePermissions: WritableSignal<string[]> = signal([]);
  private readonly permissionTypes: MatrixPermissionType[] = ["read", "create", "update", "delete", "export", "import"];

  private role: Role = this.config.data.role;

  public notificationPermissions = signal<NotificationPermission[]>([]);

  public form: FormGroup = new FormGroup({
    label: new FormControl(this.role.label, Validators.required),
    description: new FormControl(this.role.description, Validators.required),
  });
  public columns = signal([
    { field: "name", header: "name" },
    { field: "read", header: "read" },
    { field: "create", header: "create" },
    { field: "update", header: "update" },
    { field: "delete", header: "delete" },
    { field: "export", header: "export" },
    { field: "import", header: "import" },
  ]);
  public permissionsMatrix: WritableSignal<MatrixRow[] | null> = signal(null);
  public requestStarted = signal(false);

  public ngOnInit() {
    this.permissions
      .getRemotePermissions()
      .pipe(first())
      .subscribe({
        next: (permissions: string[]) => {
          this.allRemotePermissions.set(permissions);
          this.createNotificationPermissions();
          this.createMatrix();
        },
      });
  }

  /**
   * Create a matrix of permissions, where each row represents a permission and
   * each column represents a permission type (read, create, update, delete, export, import).
   * The matrix is sorted alphabetically by permission name in the current language.
   */
  private createMatrix() {
    const assignedPermissions = new Set(this.role.permissions);
    const matrixBySection = new Map<string, MatrixRow>();
    const matrixRowTitles: string[] = [];
    this.allRemotePermissions()
      .filter((permissionName) => !this.isNotificationCategoryPermission(permissionName))
      .forEach((permissionName) => {
        const [permissionSection, action, ...remainingParts] = permissionName.split(":");
        if (!permissionSection || remainingParts.length || !this.isPermissionType(action)) {
          throw new Error(`Unsupported permission name: ${permissionName}`);
        }

        const permissionSectionRow = matrixBySection.get(permissionSection) ?? { name: permissionSection };
        permissionSectionRow[action] = {
          permissionName,
          match: assignedPermissions.has(permissionName as PERMISSION),
        };
        matrixBySection.set(permissionSection, permissionSectionRow);
      });
    this.permissionsMatrix.set(Array.from(matrixBySection.values()));
    matrixBySection.forEach((_, permissionSection) => {
      matrixRowTitles.push(`role_management.form.${permissionSection}`);
    });

    // Get current translations to sort alphabetically in current language
    this.translate
      .get(matrixRowTitles)
      .pipe(first())
      .subscribe({
        next: (translations) => {
          this.permissionsMatrix.update((matrix) => {
            // Sort alphabetically:
            matrix?.sort((a, b) => {
              return translations[`role_management.form.${a.name}`] < translations[`role_management.form.${b.name}`]
                ? -1
                : 1;
            });
            return matrix;
          });
        },
      });
  }

  private createNotificationPermissions() {
    const assignedPermissions = new Set(this.role.permissions);
    this.notificationPermissions.set(
      this.allRemotePermissions()
        .filter((permissionName) => this.isNotificationCategoryPermission(permissionName))
        .map((permissionName) => ({
          permissionName,
          category: notificationPermissionCategoryMapping[permissionName.split(":")[1]],
          match: assignedPermissions.has(permissionName as PERMISSION),
        }))
    );
  }

  private isNotificationCategoryPermission(permissionName: string): boolean {
    const [entity, action, ...remainingParts] = permissionName.split(":");
    return entity === "notification" && Boolean(action) && !remainingParts.length && !this.isPermissionType(action);
  }

  /**
   * Checks whether a permission action can be represented by the matrix.
   */
  private isPermissionType(action: string): action is MatrixPermissionType {
    return this.permissionTypes.includes(action as MatrixPermissionType);
  }

  /**
   * Saves the role based on the current action type.
   * If the action type is "add", it calls the addRole method.
   * If the action type is "edit", it calls the editRole method.
   */
  public saveRole() {
    switch (this.actionType) {
      case "add":
        this.addRole();
        break;
      case "edit":
        this.editRole();
        break;
      default:
        throw new Error("Unknown action type");
    }
  }

  /**
   * Edits the role based on the current form values and closes the dialog.
   * The request is only sent if the request is not already in progress.
   */
  private editRole() {
    const editedPermissions = this.buildPermissionsList();
    const description = this.form.get("description")?.value;
    const label = this.form.get("label")?.value;

    if (!this.requestStarted()) {
      this.role.properties = {
        label,
        description,
        permissions: editedPermissions,
      };
      this.requestStarted.set(true);

      this.rolesService.editRole(this.role).subscribe({
        next: () => {
          this.requestStarted.set(false);
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant("role_management.form.success_edit"),
            life: 4500,
          });
          this.ref.close(true);
        },
        error: () => {
          this.requestStarted.set(false);
        },
      });
    }
  }

  /**
   * Adds a new role based on the current form values and closes the dialog.
   * The request is only sent if the request is not already in progress.
   * @param roleName The name of the role.
   * @param description The description of the role.
   */
  private addRole() {
    const roleName = this.form.get("label")?.value;
    const description = this.form.get("description")?.value;
    if (roleName && description) {
      const editedPermissions = this.buildPermissionsList();
      this.role.properties = {
        label: roleName,
        description: description,
        permissions: editedPermissions,
      };
      if (!this.requestStarted()) {
        this.requestStarted.set(true);
        this.rolesService.createRole(this.role).subscribe({
          next: () => {
            this.requestStarted.set(false);
            this.message.add({
              key: "global",
              severity: "success",
              summary: this.translate.instant("common.success"),
              detail: this.translate.instant("role_management.form.success_add"),
              life: 4500,
            });
            this.ref.close(true);
          },
          error: () => {
            this.requestStarted.set(false);
          },
        });
      }
    }
  }

  /**
   * Builds a list of permission names based on the current state of the form.
   * A permission is added to the list if the corresponding checkbox is checked.
   * @returns A list of permission names.
   */
  private buildPermissionsList() {
    const rolePermissions: PERMISSION[] = [];

    this.permissionsMatrix()?.forEach((permissionSection) => {
      this.permissionTypes.forEach((permissionType) => {
        const permission = permissionSection[permissionType];
        if (permission?.match) {
          rolePermissions.push(permission.permissionName as PERMISSION);
        }
      });
    });

    this.notificationPermissions()
      .filter((permission) => permission.match)
      .forEach((permission) => rolePermissions.push(permission.permissionName as PERMISSION));

    return rolePermissions;
  }
}
